import { EspAccount, SendLimitDefinition } from 'modules/graphql/types';
import { StyledTooltip } from '../components/Details/AccountsSection.styled';
import {
  DEFAULT_SENDER_TEXT,
  SERVICE_PROVIDER_NAMES,
  WHATSAPP_SEND_LIMIT_DISPLAY_MAPPING,
} from '../constants';
import { isSparkPostProvider } from './serviceProviders';

export const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  count === 1 ? singular : plural;

export const pluralResourcesText = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${pluralize(count, singular, plural)}`;

export const iterableLoveText = (sendingPlatformName: string) =>
  `Iterable + ${sendingPlatformName} = ❤️`;

export const composeEmailAccountDetailsText = (
  espAccount: EspAccount,
  includeAccountType = false,
) => {
  const idText = `ID ${espAccount.id}`;
  const defaultText = espAccount.isDefault ? DEFAULT_SENDER_TEXT : '';
  const accountTypeText = includeAccountType
    ? SERVICE_PROVIDER_NAMES[espAccount.esp as keyof typeof SERVICE_PROVIDER_NAMES]
    : '';
  const binding = isSparkPostProvider(espAccount.esp)
    ? `${espAccount?.binding || 'Default pool'}`
    : '';
  return [idText, accountTypeText, defaultText, binding].filter(Boolean).join(' • ');
};

export const composeSmsAccountDetailsText = (espAccount: EspAccount) => {
  const { esp, id, isSenderIterable } = espAccount;
  const idText = `ID ${id}`;
  const accountTypeText = isSenderIterable ? `Connected to ${esp}` : '';
  return [idText, accountTypeText].filter(Boolean).join(' • ');
};

export const disconnectDisabledText = (isDefault: boolean, hasChannels: boolean) => {
  let text = 'Before you can disconnect this account, you’ll need to ';
  if (isDefault && hasChannels) {
    text +=
      'set another account to use for new message channels and update the message channels that rely on it.';
  } else if (isDefault) {
    text += 'set another account to use for new message channels.';
  } else if (hasChannels) {
    text += 'update the message channels that rely on it.';
  }
  return text;
};

export const getWhatsAppSendLimitContent = <T extends JSX.Element | string>(
  limit?: SendLimitDefinition,
  withTooltip?: boolean,
  addPrefix?: boolean,
): T => {
  switch (limit) {
    case 'Limit100K':
    case 'Limit10K':
    case 'Limit1K':
    case 'Limit250':
    case 'Limit50': {
      const PREFIX = 'Sends limited to ';
      const SUFFIX = ' unique users/24 h';
      const limitText = limit.replace('Limit', '').toLowerCase();

      return (
        withTooltip ? (
          <>
            {addPrefix && PREFIX}
            <StyledTooltip
              tooltipContent={WHATSAPP_SEND_LIMIT_DISPLAY_MAPPING[limit]?.tier}
              isUnderlined
            >
              {limitText}
              {SUFFIX}
            </StyledTooltip>
          </>
        ) : (
          `${addPrefix ? PREFIX : ''}${limitText}${SUFFIX}`
        )
      ) as T;
    }
    case 'Unlimited': {
      const LIMIT_TEXT = 'Unlimited sends';
      return (
        withTooltip ? (
          <StyledTooltip
            tooltipContent={WHATSAPP_SEND_LIMIT_DISPLAY_MAPPING[limit]?.tier}
            isUnderlined
          >
            {LIMIT_TEXT}
          </StyledTooltip>
        ) : (
          LIMIT_TEXT
        )
      ) as T;
    }
    case 'LimitNa':
    default:
      return 'Send limit unavailable' as T;
  }
};
